import type { ComponentType, LazyExoticComponent } from "react";
import { Suspense, lazy } from "react";

import { Route, Routes } from "react-router";

import { PATHS } from "@/app/routes/paths";
import type { PermissionCode } from "@/core/authz/permissions";
import { RequireAuth } from "@/core/middleware/RequireAuth";
import { RequirePermission } from "@/core/middleware/RequirePermission";
import { AppShell } from "@/layouts/AppShell";
import { NotFoundPage } from "@/shared/components/NotFoundPage";
import { PageLoader } from "@/shared/components/PageLoader";
import { RouteErrorBoundary } from "@/shared/components/RouteErrorBoundary";

type LazyPage = LazyExoticComponent<ComponentType>;

/**
 * Route table with route-level code splitting: every module chunk loads on
 * demand. Page titles are resolved from the navigation config (navigation.ts).
 *
 * `permission` is the `view` grant the screen needs; a route without one is
 * open to every signed-in user. Screens the sidebar also lists carry the
 * same code as their nav entry, so a hidden menu row and a typed-in URL
 * agree with each other.
 */
const SsoCallbackPage = lazy(() =>
  import("@/features/auth/SsoCallbackPage").then((m) => ({ default: m.SsoCallbackPage })),
);
const LoginPage = lazy(() =>
  import("@/features/auth/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/features/auth/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import("@/features/auth/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })),
);

const routes: Array<{ path: string; Page: LazyPage; permission?: PermissionCode }> = [
  {
    // The landing screen every operator is bounced to after sign-in.
    path: PATHS.dashboard,
    Page: lazy(() =>
      import("@/features/dashboard/Dashboard").then((m) => ({ default: m.Dashboard })),
    ),
  },
  {
    path: PATHS.userManagement.userAccess,
    permission: "user-management.view",
    Page: lazy(() =>
      import("@/features/user-management/UserManagementPage").then((m) => ({
        default: m.UserManagementPage,
      })),
    ),
  },
  {
    path: PATHS.wheelData.transactions,
    permission: "wheel-register.view",
    Page: lazy(() =>
      import("@/features/wheel-data/WheelDataModule").then((m) => ({ default: m.WheelDataModule })),
    ),
  },
  {
    path: PATHS.wheelData.transaction,
    permission: "wheel-register.view",
    Page: lazy(() =>
      import("@/features/wheel-data/WheelsetDetailRoute").then((m) => ({
        default: m.WheelsetDetailRoute,
      })),
    ),
  },
  {
    path: PATHS.machineOps.oxycut,
    permission: "wheel-maintenance.view",
    Page: lazy(() =>
      import("@/features/oxycut/AutomationList").then((m) => ({ default: m.AutomationList })),
    ),
  },
  {
    path: PATHS.machineOps.atl,
    permission: "wheel-maintenance.view",
    Page: lazy(() => import("@/features/atl/ATLList").then((m) => ({ default: m.ATLList }))),
  },
  {
    path: PATHS.machineOps.vtl,
    permission: "wheel-maintenance.view",
    Page: lazy(() => import("@/features/vtl/VTLList").then((m) => ({ default: m.VTLList }))),
  },
  {
    path: PATHS.machineOps.pressin,
    permission: "wheel-maintenance.view",
    Page: lazy(() =>
      import("@/features/pressin/PressinList").then((m) => ({ default: m.PressinList })),
    ),
  },
  {
    path: PATHS.machineOps.wheelLathe,
    permission: "wheel-maintenance.view",
    Page: lazy(() =>
      import("@/features/wheel-lathe/WheelLatheList").then((m) => ({ default: m.WheelLatheList })),
    ),
  },
  {
    path: PATHS.machineOps.bearingRtc,
    permission: "wheel-maintenance.view",
    Page: lazy(() =>
      import("@/features/bearing-rtc/BearingRTCList").then((m) => ({ default: m.BearingRTCList })),
    ),
  },
  {
    path: PATHS.machineOps.ctbrMaintenance,
    permission: "wheel-maintenance.view",
    Page: lazy(() =>
      import("@/features/ctbr-maintenance/CTBRMaintenanceList").then((m) => ({
        default: m.CTBRMaintenanceList,
      })),
    ),
  },
  {
    /* The inspection screens are split across two modules: this one lists
       receipt batches and their wheelsets, so it reads the register, while
       the measurement screens below record findings against a wheelset
       already in the maintenance pipeline. */
    path: PATHS.inspection.wheelsets,
    permission: "wheel-register.view",
    Page: lazy(() =>
      import("@/features/inspection/InspectionBatchList").then((m) => ({
        default: m.InspectionBatchList,
      })),
    ),
  },
  {
    path: PATHS.inspection.defective,
    permission: "wheel-maintenance.view",
    Page: lazy(() =>
      import("@/features/defective-wheelset/DefectiveWheelsetList").then((m) => ({
        default: m.DefectiveWheelsetList,
      })),
    ),
  },
  {
    path: PATHS.inspection.cmtCheck,
    permission: "wheel-maintenance.view",
    Page: lazy(() =>
      import("@/features/cmt-check/CMTCheckList").then((m) => ({ default: m.CMTCheckList })),
    ),
  },
  {
    path: PATHS.inspection.airGauge,
    permission: "wheel-maintenance.view",
    Page: lazy(() =>
      import("@/features/air-gauge/AirGaugeList").then((m) => ({ default: m.AirGaugeList })),
    ),
  },
  {
    path: PATHS.maintenance.subcomponent,
    permission: "wheel-maintenance.view",
    Page: lazy(() =>
      import("@/features/subcomponents/SubcomponentPage").then((m) => ({
        default: m.SubcomponentPage,
      })),
    ),
  },
  {
    path: PATHS.assemblyDispatch.assembly,
    permission: "wheel-assembly.view",
    Page: lazy(() =>
      import("@/features/assembly/AssemblyList").then((m) => ({ default: m.AssemblyList })),
    ),
  },
  {
    path: PATHS.assemblyDispatch.adjustment,
    permission: "wheel-assembly.view",
    Page: lazy(() =>
      import("@/features/adjustment/AdjustmentList").then((m) => ({ default: m.AdjustmentList })),
    ),
  },
  {
    path: PATHS.assemblyDispatch.dispatch,
    permission: "wheel-assembly.view",
    Page: lazy(() =>
      import("@/features/dispatch/DispatchList").then((m) => ({ default: m.DispatchList })),
    ),
  },
  {
    path: PATHS.reports,
    permission: "reports.view",
    Page: lazy(() =>
      import("@/features/reports/ReportsPage").then((m) => ({ default: m.ReportsPage })),
    ),
  },
  {
    // One screen, tab per catalogue; the tab slug is the sub-path so every
    // existing /masters/<catalogue> link still resolves.
    path: PATHS.masters.root,
    permission: "master-management.view",
    Page: lazy(() =>
      import("@/features/masters/MastersPage").then((m) => ({ default: m.MastersPage })),
    ),
  },
  {
    path: PATHS.masters.root + "/:tab",
    permission: "master-management.view",
    Page: lazy(() =>
      import("@/features/masters/MastersPage").then((m) => ({ default: m.MastersPage })),
    ),
  },
];

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path={PATHS.auth.login}
        element={
          <RouteErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <LoginPage />
            </Suspense>
          </RouteErrorBoundary>
        }
      />
      <Route
        path={PATHS.auth.forgotPassword}
        element={
          <RouteErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <ForgotPasswordPage />
            </Suspense>
          </RouteErrorBoundary>
        }
      />
      <Route
        path={PATHS.auth.resetPassword}
        element={
          <RouteErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <ResetPasswordPage />
            </Suspense>
          </RouteErrorBoundary>
        }
      />
      <Route
        path={PATHS.auth.ssoCallback}
        element={
          <RouteErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <SsoCallbackPage />
            </Suspense>
          </RouteErrorBoundary>
        }
      />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {routes.map(({ path, Page, permission }) => {
          /* Guarding inside the boundary keeps a 403 a rendered screen
             rather than something the error boundary has to interpret. */
          const page = (
            <Suspense fallback={<PageLoader />}>
              <Page />
            </Suspense>
          );
          return (
            <Route
              key={path}
              path={path}
              element={
                <RouteErrorBoundary>
                  {permission ? (
                    <RequirePermission permission={permission}>{page}</RequirePermission>
                  ) : (
                    page
                  )}
                </RouteErrorBoundary>
              }
            />
          );
        })}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
